import { defineStore } from 'pinia';
import { ref, watch, computed } from 'vue';
import { GetEmployeeLookup } from '../services/ProjectTagging/GetEmployeeLookup';
import { GetProjects } from '../services/ProjectTagging/GetProjects';
import { checkBlankSpace } from '../helpers/inputValidator';
import { GetProjectStatuses } from '../services/ProjectTagging/GetProjectStatuses';
import { GetProjectEmployees } from '../services/ProjectTagging/GetProjectEmployees';
import { GetRouteAccess } from '@/services/Users/GetRouteAccess';
import { PostProjectLogs } from '../services/ProjectTagging/PostProjectLogs';
import { GetProjectLogs } from '../services/ProjectTagging/GetProjectLogs';
import { ReportTypesEnum } from '../enums/reportTypes';
import { GetReportsList } from '../services/Reports/GetReportsList';

export const useProjectTaggingStore = defineStore('projectTagging', () => {
  const projects = ref([]);
  const projectsPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const drawerPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const projectCode = ref('');
  const projectName = ref('');
  const projectNameDescription = ref('');
  const projectStatus = ref(true);
  const projectId = ref('');
  const isLoading = ref(false);
  const toastState = ref(false);
  const toastEditState = ref(false);
  const toastAssignAllState = ref(false);
  const toastRemoveAllState = ref(false);
  const toastProjectName = ref('');
  const createModalState = ref(false);
  const editModalState = ref(false);
  const tabCounter = ref({
    activeCounter: 0,
    inactiveCounter: 0,
  });
  const projectStatusOptions = ref([]);
  const filterTab = ref('Active');
  const drawerState = ref(false);
  const drawerSearch = ref('');
  const drawerEmployeeList = ref([]);
  const drawerAllBtnLoader = ref(false);
  const drawerListContainerLoader = ref(false);
  const searchFilter = ref(['Name', 'Code']);
  const searchString = ref('');
  const drawerFilterOptions = ref([
    { text: 'Last Name', value: 'LastName' },
    { text: 'First Name', value: 'FirstName' },
    { text: 'Employee Id', value: 'EmployeeId' },
  ]);
  const localSelectedDrawerFilter = ref('');
  const selectedDrawerFilter = ref('');
  const localSelectedStatusFilter = ref('');
  const selectedStatusFilter = ref('');
  const drawerReportState = ref(false);
  const reports = ref([]);
  const reportsPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const viewProjectListDrawer = ref(false);
  const viewEmployeeListDrawer = ref(false);

  const reportsHeaders = ref([]);
  const reportsTitle = ref('No Project Selected');

  const selectedEmployees = ref([]);

  const getProjects = async (payload) => {
    isLoading.value = true;

    try {
      const response = await GetProjects({ ...payload });

      if (response.errors) {
        projects.value = [];
      }
      projects.value = response.data;
      projectsPagination.value = response.pagination;
    } finally {
      isLoading.value = false;
    }
  };
  const getEmployeeLookup = async (params) => {
    await GetEmployeeLookup(projectId.value, params)
      .then((response) => {
        drawerEmployeeList.value = response.data;
        drawerPagination.value = response.pagination;
      })
      .finally(() => {
        drawerListContainerLoader.value = false;
        const getProjectParams = {
          SearchString: searchString.value,
          FilterBy: searchFilter.value,
          RowsPerPage: projectsPagination.value.itemsPerPage,
          PageNumber: projectsPagination.value.currentPage,
          StatusIds: selectedStatusFilter.value,
        };
        if (!checkBlankSpace(searchString.value)) {
          getProjects(getProjectParams);
        } else {
          getProjects({
            RowsPerPage: projectsPagination.value.itemsPerPage,
            PageNumber: projectsPagination.value.currentPage,
            StatusIds: selectedStatusFilter.value,
          });
        }
      });
  };
  const getProjectStatuses = () => {
    GetProjectStatuses().then((response) => {
      projectStatusOptions.value = formatProjectStatuses(response);
    });
  };
  const formatProjectStatuses = (data) => {
    return data.map((item) => {
      return {
        text: item.name,
        value: item.id,
      };
    });
  };
  const isFeatureFlagOn = ref(false);
  const viewProject = ref(false);
  const selectedProject = ref();
  const selectedProjectMembers = ref([]);
  const selectedProjectMembersLoading = ref(false);
  const selectedProjectMembersPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 50 });
  const getProjectEmployees = async (params) => {
    selectedProjectMembersLoading.value = true;
    const response = await GetProjectEmployees(params);

    if (response.errors) return;

    selectedProjectMembers.value = response.data;
    selectedProjectMembersPagination.value = response.pagination;
    selectedProjectMembersLoading.value = false;
  };
  watch(selectedProject, (newSelectedProject) => {
    if (!newSelectedProject) return;
    const params = {
      ProjectID: newSelectedProject.id,
      PageNumber: selectedProjectMembersPagination.value.currentPage,
      RowsPerPage: selectedProjectMembersPagination.value.itemsPerPage,
    };
    getProjectEmployees(params);
  });

  const confirmedProject = ref({});
  const confirmedEmployees = ref([]);
  const projectLogPayload = ref({});
  const isConfirmedProjectEmpty = computed(() => Object.keys(confirmedProject.value).length < 1);
  const isConfirmedEmployeesEmpty = computed(() => confirmedEmployees.value.length < 1);
  const showEmployees = computed(() => {
    if (isConfirmedEmployeesEmpty.value && confirmedProject.value.assigneesCount > 0 && !isConfirmedProjectEmpty.value) return 'All Employees';
    if (confirmedEmployees.value.length == 1) return `${confirmedEmployees.value[0].lastName}, ${confirmedEmployees.value[0].firstName}`;
    if (confirmedEmployees.value.length > 1) return `${confirmedEmployees.value.length} Employees Selected`;
    return '';
  });

  const isFiltersUpdated = ref(false);
  watch([confirmedProject, confirmedEmployees], () => {
    isFiltersUpdated.value = true;
  });
  const getProjectLogs = async (payload) => {
    isLoading.value = true;
    drawerReportState.value = false;
    projectLogPayload.value = { ...projectLogPayload.value, ...payload };
    const searchCriteriaId = ref();
    await PostProjectLogs(confirmedProject.value.id, projectLogPayload.value).then((data) => {
      searchCriteriaId.value = data.searchCriteriaId;
    });
    const getParams = {
      ProjectID: confirmedProject.value.id,
      Id: searchCriteriaId.value,
    };
    await GetProjectLogs(getParams).then((data) => {
      reports.value = data.data;
      reportsPagination.value = data.pagination;
      reportsTitle.value = confirmedProject.value.name;
    });
    isLoading.value = false;
    isFiltersUpdated.value = false;
  };

  const routeAccessWithId = ref({
    isAuthenticated: false,
    isAllowedToAccess: false,
    redirectUrl: '',
    accessLevelStatus: '',
  });
  const getClientWithId = (moduleId) => {
    GetRouteAccess(moduleId).then((response) => {
      routeAccessWithId.value = response;
    });
  };

  // Exported Files
  const exportedLogs = ref([]);
  const exportedLogsPagination = ref({ currentPage: 1, totalPages: 0, totalItems: 0, itemsPerPage: 10 });
  const isExportedLogsLoading = ref(false);
  const isExportedLogsInitialState = ref(true);
  const getExportedProjectTagging = async ({ PageNumber, RowsPerPage }) => {
    isExportedLogsLoading.value = true;
    try {
      const response = await GetReportsList({
        PageNumber,
        RowsPerPage,
        ReportType: ReportTypesEnum.projectTagging,
      });
      if (response.errors) {
        exportedLogs.value = [];
        return;
      }
      exportedLogs.value = response.data;
      exportedLogsPagination.value = response.pagination;
    } finally {
      isExportedLogsInitialState.value = false;
      isExportedLogsLoading.value = false;
    }
  };

  return {
    projects,
    projectsPagination,
    getProjects,
    projectCode,
    projectName,
    projectStatus,
    tabCounter,
    toastState,
    toastProjectName,
    createModalState,
    filterTab,
    editModalState,
    toastEditState,
    projectId,
    drawerState,
    drawerSearch,
    drawerEmployeeList,
    getEmployeeLookup,
    isFeatureFlagOn,
    drawerPagination,
    drawerAllBtnLoader,
    toastAssignAllState,
    toastRemoveAllState,
    drawerListContainerLoader,
    searchFilter,
    searchString,
    projectStatusOptions,
    getProjectStatuses,
    drawerFilterOptions,
    localSelectedDrawerFilter,
    selectedDrawerFilter,
    localSelectedStatusFilter,
    selectedStatusFilter,
    viewProject,
    selectedProject,
    selectedProjectMembers,
    selectedProjectMembersLoading,
    selectedProjectMembersPagination,
    getProjectEmployees,
    projectNameDescription,
    drawerReportState,
    reports,
    reportsPagination,
    viewProjectListDrawer,
    viewEmployeeListDrawer,
    confirmedProject,
    confirmedEmployees,
    showEmployees,
    isFiltersUpdated,
    isConfirmedProjectEmpty,
    isConfirmedEmployeesEmpty,
    reportsHeaders,
    reportsTitle,
    getProjectLogs,
    isLoading,
    selectedEmployees,
    getClientWithId,
    routeAccessWithId,
    exportedLogs,
    exportedLogsPagination,
    isExportedLogsLoading,
    isExportedLogsInitialState,
    getExportedProjectTagging,
  };
});
